import React, { ReactElement } from 'react';
import { View, Text, StyleProp, TextStyle } from 'react-native';
import { Tappable, TappableProps } from 'src/components/Tappable/Tappable';
import { isString } from 'src/utils/string';
import useStyles from './Button.styles';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'outline';
export type ButtonSize = 'l' | 'm' | 's';
export type ButtonProps = TappableProps & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  before?: ReactElement;
  after?: ReactElement;
  textStyle?: StyleProp<TextStyle>;
  fluid?: boolean;
};

/**
 * Accessible button component
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'l',
  disabled = false,
  before = undefined,
  after = undefined,
  style,
  textStyle,
  fluid = true,
  ...props
}) => {
  const s = useStyles({ variant, size, fluid });

  return (
    <Tappable
      {...props}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      style={[s.root, disabled && s.disabled, style]}
      disabled={disabled}
      borderless
      hoverable
    >
      <View style={s.content}>
        {(before || after) && <View style={[s.side, s.before]}>{before}</View>}
        {isString(children) ? (
          <Text style={[s.inner, textStyle]} numberOfLines={1}>
            {children}
          </Text>
        ) : (
          <View style={[s.inner]}>{children}</View>
        )}
        {(before || after) && <View style={[s.side, s.after]}>{after}</View>}
      </View>
    </Tappable>
  );
};
